import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import axios from 'axios';

interface User {
  id: number;
  email: string;
  username: string;
  full_name?: string;
  avatar_url?: string;
  is_admin: boolean;
  admin_role?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

/**
 * Only a real answer from the server means "this session is dead".
 *
 * A dropped connection, a timeout, a 429 or a 5xx says nothing about whether
 * the credentials are still valid. Treating those as auth failures is what
 * signed the panel out on every transient network blip.
 */
const isDefinitiveAuthFailure = (error: any): boolean => {
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    localStorage.removeItem('lifeshare_admin_token');
    localStorage.removeItem('lifeshare_admin_refresh_token');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // One refresh at a time. Loading a page fires many requests at once; when the
  // access token has expired they all 401 together and each used to start its
  // own refresh, so a single failure among them took the whole session down.
  const refreshInFlight = useRef<Promise<string | null> | null>(null);

  // Auto-refresh token function
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }

    const refreshToken = localStorage.getItem('lifeshare_admin_refresh_token');
    if (!refreshToken) {
      logout();
      return null;
    }

    const attempt = (async (): Promise<string | null> => {
      try {
        const response = await axios.post('https://api.lifeshare.app/api/v1/auth/refresh', {
          refresh_token: refreshToken
        });

        const { access_token } = response.data;
        setToken(access_token);
        localStorage.setItem('lifeshare_admin_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        return access_token;
      } catch (error: any) {
        if (isDefinitiveAuthFailure(error)) {
          console.warn('🔐 Refresh token rejected by the server — signing out');
          logout();
        } else {
          // Offline, timed out, rate limited or a server error: the session is
          // almost certainly still valid, so keep it and let the caller retry.
          console.warn('⚠️ Token refresh could not complete, keeping session:', error?.message);
        }
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    refreshInFlight.current = attempt;
    return attempt;
  }, [logout]);

  useEffect(() => {
    // Setup axios request interceptor to add token to every request
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('lifeshare_admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Setup axios response interceptor for auto-refresh
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && error.config && !error.config._retry) {
          error.config._retry = true;
          
          const newToken = await refreshAccessToken();
          if (newToken) {
            error.config.headers.Authorization = `Bearer ${newToken}`;
            return axios.request(error.config);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [refreshAccessToken]);

  useEffect(() => {
    // Check for stored token on app start
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('lifeshare_admin_token');
      const storedRefreshToken = localStorage.getItem('lifeshare_admin_refresh_token');
      
      console.log('🔐 AUTH INIT:', {
        hasToken: !!storedToken,
        hasRefreshToken: !!storedRefreshToken,
        tokenLength: storedToken?.length
      });
      
      if (storedToken) {
        setToken(storedToken);
        setRefreshToken(storedRefreshToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        
        // Validate token and get user info
        try {
          const profileResponse = await axios.get('https://api.lifeshare.app/api/v1/users/me');
          const userData = profileResponse.data;
          
          // Check if user is admin
          if (userData.is_admin) {
            const userObj = {
              id: userData.id,
              email: userData.email,
              username: userData.username,
              full_name: userData.full_name,
              avatar_url: userData.avatar_url,
              is_admin: userData.is_admin,
              admin_role: userData.admin_role || 'admin'
            };
            setUser(userObj);
          } else {
            // Not admin, clear token
            setUser(null);
            setToken(null);
            setRefreshToken(null);
            localStorage.removeItem('lifeshare_admin_token');
            localStorage.removeItem('lifeshare_admin_refresh_token');
            delete axios.defaults.headers.common['Authorization'];
          }
        } catch (error: any) {
          // If token expired (401), try to refresh it
          if (error.response?.status === 401 && storedRefreshToken) {
            try {
              const refreshResponse = await axios.post('https://api.lifeshare.app/api/v1/auth/refresh', {
                refresh_token: storedRefreshToken
              });
              
              const { access_token } = refreshResponse.data;
              setToken(access_token);
              localStorage.setItem('lifeshare_admin_token', access_token);
              axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
              
              // Try again with new token
              try {
                const retryResponse = await axios.get('https://api.lifeshare.app/api/v1/users/me');
                const retryUserData = retryResponse.data;
                if (retryUserData.is_admin) {
                  setUser({
                    id: retryUserData.id,
                    email: retryUserData.email,
                    username: retryUserData.username,
                    full_name: retryUserData.full_name,
                    avatar_url: retryUserData.avatar_url,
                    is_admin: retryUserData.is_admin,
                    admin_role: retryUserData.admin_role || 'admin'
                  });
                  console.log('✅ Token refreshed successfully, user authenticated');
                  return; // Success, don't logout
                }
              } catch (retryError) {
                console.error('Retry after refresh failed:', retryError);
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
            }
          }
          // Discard the stored session only when the server actually rejected it.
          // After a network failure the tokens are almost certainly still good,
          // so they stay in localStorage and the next reload signs straight back
          // in rather than asking for the password again.
          if (isDefinitiveAuthFailure(error)) {
            console.warn('🔐 Stored session rejected by the server — clearing it');
            setUser(null);
            setToken(null);
            setRefreshToken(null);
            localStorage.removeItem('lifeshare_admin_token');
            localStorage.removeItem('lifeshare_admin_refresh_token');
            delete axios.defaults.headers.common['Authorization'];
          } else {
            console.warn('⚠️ Could not verify the session (network) — stored tokens kept');
          }
        }
      } else {
        console.log('ℹ️ No stored token found');
      }
      setIsLoading(false);
      console.log('✅ Auth initialization complete');
    };
    
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Login with admin credentials
      const loginResponse = await axios.post('https://api.lifeshare.app/api/v1/auth/login', {
        email: email,
        password: password,
        remember_me: rememberMe
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { access_token, refresh_token } = loginResponse.data;
      
      // Store tokens and set default header
      setToken(access_token);
      setRefreshToken(refresh_token);
      localStorage.setItem('lifeshare_admin_token', access_token);
      // ALWAYS store refresh token (rememberMe only affects token expiration on backend)
      if (refresh_token) {
        localStorage.setItem('lifeshare_admin_refresh_token', refresh_token);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Fetch user profile to verify admin status
      try {
        const profileResponse = await axios.get('https://api.lifeshare.app/api/v1/users/me');
        const userData = profileResponse.data;
        
        // Check if user is admin
        if (!userData.is_admin) {
          throw new Error('Unauthorized: Admin access required');
        }
        
        setUser({
          id: userData.id,
          email: userData.email,
          username: userData.username,
          full_name: userData.full_name,
          avatar_url: userData.avatar_url,
          is_admin: userData.is_admin,
          admin_role: userData.admin_role || 'admin'
        });

        return true;
      } catch (profileError) {
        console.error('Profile fetch error:', profileError);
        // Clear token if profile fetch fails
        logout();
        return false;
      }
      
    } catch (error: any) {
      console.error('Login failed:', error);
      const errorMsg = error.response?.data?.detail || 'Login failed';
      console.error('Login error details:', errorMsg);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const isAuthenticated = !!token && !!user;

  // Debug log for authentication status
  React.useEffect(() => {
    console.log('🔓 Auth Status Changed:', {
      hasUser: !!user,
      hasToken: !!token,
      isAuthenticated,
      username: user?.username
    });
  }, [user, token, isAuthenticated]);

  const value: AuthContextType = {
    user,
    token,
    refreshToken,
    login,
    logout,
    isLoading,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
